/**
 * KlientListe - Oversikt over alle klienter
 */

import React, { useState, useEffect } from 'react';
import crmApi from '../api/crm';
import KYCSkjema from '../components/KYCSkjema';
import './KlientListe.css';

export default function KlientListe() {
  console.log('🚀 KlientListe component rendering...');

  const [klienter, setKlienter] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('alle'); // 'alle', 'klient', 'forretningsforbindelse'
  const [sokTekst, setSokTekst] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedKlient, setSelectedKlient] = useState(null);
  const [newKlient, setNewKlient] = useState({
    navn: '',
    epost: '',
    telefon: '',
    adresse: '',
    org_nr: '',
    type: 'klient'
  });
  const [editKlient, setEditKlient] = useState({
    navn: '',
    epost: '',
    telefon: '',
    adresse: '',
    org_nr: '',
    type: 'klient'
  });
  const [showImportModal, setShowImportModal] = useState(false);
  const [excelFile, setExcelFile] = useState(null);
  const [showKYCModal, setShowKYCModal] = useState(false);
  const [kycKlient, setKycKlient] = useState(null);
  const [kycStatuser, setKycStatuser] = useState({});

  useEffect(() => {
    console.log('📦 useEffect running - calling lastKlienter()');
    lastKlienter();
  }, [typeFilter]); // Oppdater når typeFilter endres

  useEffect(() => {
    // Last KYC-statuser for alle klienter
    if (klienter.length > 0) {
      lastKYCStatuser();
    }
  }, [klienter]);

  const lastKlienter = async () => {
    console.log('🔄 lastKlienter() called');
    try {
      setLoading(true);
      console.log('⏳ Fetching klienter from API...');

      // Send type-filter til API hvis ikke 'alle'
      const filters = typeFilter !== 'alle' ? { type: typeFilter } : {};
      const data = await crmApi.getKlienter(filters);
      console.log('✅ Klienter data received:', data);

      // Sjekk for feil i responsen
      if (data && data.error) {
        console.error('❌ API returned error:', data.error);
        alert('Kunne ikke laste klienter: ' + data.error);
        setKlienter([]);
        return;
      }

      // API returnerer {klienter: [...]} ikke bare [...]
      const klienterArray = Array.isArray(data) ? data : (data.klienter || []);
      console.log('📋 Setting klienter array:', klienterArray);

      // Extra safety check
      if (!Array.isArray(klienterArray)) {
        console.error('❌ klienterArray is not an array:', klienterArray);
        setKlienter([]);
        return;
      }

      setKlienter(klienterArray);
    } catch (error) {
      console.error('❌ Feil ved lasting av klienter:', error);
      alert('Kunne ikke laste klienter: ' + error.message);
      setKlienter([]); // Sett tom array ved feil
    } finally {
      console.log('✅ Loading complete, setting loading=false');
      setLoading(false);
    }
  };

  const lastKYCStatuser = async () => {
    try {
      const statuser = {};

      // Hent KYC-status for hver klient
      await Promise.all(
        klienter.map(async (klient) => {
          try {
            const response = await fetch(`${crmApi.baseUrl}/api/kyc?klient_id=${klient.id}`, {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('access_token')}`
              }
            });

            if (response.ok) {
              const data = await response.json();
              statuser[klient.id] = data.kyc;
            }
            // Stille feil hvis KYC-endepunkt ikke er implementert (401/404)
          } catch (err) {
            // KYC-endepunkt ikke implementert enda - stille feil
          }
        })
      );

      setKycStatuser(statuser);
    } catch (error) {
      console.error('Feil ved lasting av KYC-statuser:', error);
    }
  };

  const handleStartKYC = (klient, e) => {
    e.stopPropagation(); // Unngå at klient-kortet klikkes
    setKycKlient(klient);
    setShowKYCModal(true);
  };

  const handleKYCFullført = async (egenerklæring) => {
    try {
      const response = await fetch(`${crmApi.baseUrl}/api/kyc`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify({
          klient_id: kycKlient.id,
          egenerklæring
        })
      });

      if (!response.ok) {
        throw new Error('Kunne ikke lagre KYC');
      }

      const result = await response.json();

      alert('✅ KYC-egenerklæring mottatt!\n\nStatus: ' + result.message);

      setShowKYCModal(false);
      setKycKlient(null);

      // Last KYC-statuser på nytt
      lastKYCStatuser();
    } catch (error) {
      console.error('Feil ved lagring av KYC:', error);
      alert('Kunne ikke lagre KYC: ' + error.message);
    }
  };

  const getKYCStatusBadge = (klientId) => {
    const kyc = kycStatuser[klientId];

    if (!kyc) {
      return <span className="kyc-badge kyc-mangler" title="KYC ikke påbegynt">❌ KYC</span>;
    }

    if (kyc.status === 'fullført' && kyc.godkjent_av_advokat) {
      return <span className="kyc-badge kyc-godkjent" title="KYC godkjent">✅ KYC</span>;
    }

    if (kyc.status === 'pågår') {
      return <span className="kyc-badge kyc-pågår" title="KYC venter på godkjenning">⏳ KYC</span>;
    }

    if (kyc.status === 'avvist') {
      return <span className="kyc-badge kyc-avvist" title="KYC avvist">⚠️ KYC</span>;
    }

    return <span className="kyc-badge kyc-mangler">❓ KYC</span>;
  };

  const handleOpprettKlient = async () => {
    if (!newKlient.navn.trim()) {
      alert('Navn er påkrevd');
      return;
    }

    try {
      await crmApi.createKlient(newKlient);
      setShowCreateModal(false);
      setNewKlient({
        navn: '',
        epost: '',
        telefon: '',
        adresse: '',
        org_nr: '',
        type: 'klient'
      });
      lastKlienter();
      alert('Klient opprettet!');
    } catch (error) {
      console.error('Feil ved oppretting:', error);
      alert('Kunne ikke opprette klient. Se konsoll for detaljer.');
    }
  };

  const handleNyKlient = () => {
    setShowCreateModal(true);
  };

  const handleImporterFraExcel = () => {
    setShowImportModal(true);
  };

  const handleExcelFileChange = (e) => {
    setExcelFile(e.target.files[0]);
  };

  const handleStartImport = async () => {
    if (!excelFile) {
      alert('Vennligst velg en Excel-fil først');
      return;
    }

    try {
      setLoading(true);
      setShowImportModal(false);

      // Les Excel-filen
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = window.XLSX.read(data, { type: 'array' });

          // Les første ark
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          const rows = window.XLSX.utils.sheet_to_json(firstSheet);

          let importert = 0;
          let feilet = 0;
          const feilmeldinger = [];

          for (const row of rows) {
            try {
              // Finn navn fra kolonner
              const navn = row['Navn'] || row['navn'] ||
                          `${row['Fornavn'] || row['fornavn'] || ''} ${row['Etternavn'] || row['etternavn'] || ''}`.trim();
              const telefon = row['Telefon'] || row['telefon'] || row['Mobil'] || row['mobil'] || '';
              const adresse = row['Adresse'] || row['adresse'] || '';
              const epost = row['E-post'] || row['epost'] || row['Email'] || row['email'] || '';
              const org_nr = row['Org.nr'] || row['org_nr'] || row['Organisasjonsnummer'] || '';
              const type = row['Type'] || row['type'] || 'klient';

              if (!navn) {
                console.log('Hopper over rad uten navn:', row);
                continue;
              }

              await crmApi.createKlient({
                navn,
                epost,
                telefon,
                adresse,
                org_nr,
                type
              });
              importert++;
            } catch (error) {
              console.error(`Feil ved import av rad:`, row, error);
              feilet++;
              const radNavn = row['Navn'] || row['navn'] || `${row['Fornavn']} ${row['Etternavn']}`.trim() || 'Ukjent';
              feilmeldinger.push(`${radNavn}: ${error.message}`);
            }
          }

          lastKlienter();

          let melding = `✅ Import fullført!\n\nImportert: ${importert}\nFeilet: ${feilet}`;
          if (feilmeldinger.length > 0 && feilmeldinger.length <= 5) {
            melding += '\n\nFeil:\n' + feilmeldinger.join('\n');
          }
          alert(melding);

          setExcelFile(null);
        } catch (error) {
          console.error('Feil ved lesing av Excel:', error);
          alert('Kunne ikke lese Excel-filen: ' + error.message);
        } finally {
          setLoading(false);
        }
      };

      reader.readAsArrayBuffer(excelFile);
    } catch (error) {
      console.error('Import feilet:', error);
      alert('Import feilet: ' + error.message);
      setLoading(false);
    }
  };

  const handleKlientKlikk = (klient) => {
    setSelectedKlient(klient);
    setEditKlient({
      navn: klient.navn || '',
      epost: klient.epost || '',
      telefon: klient.telefon || '',
      adresse: klient.adresse || '',
      org_nr: klient.org_nr || '',
      type: klient.type || 'klient'
    });
    setShowEditModal(true);
  };

  const handleOppdaterKlient = async () => {
    if (!editKlient.navn.trim()) {
      alert('Navn er påkrevd');
      return;
    }

    try {
      await crmApi.updateKlient(selectedKlient.id, editKlient);
      setShowEditModal(false);
      setSelectedKlient(null);
      lastKlienter();
      alert('Klient oppdatert!');
    } catch (error) {
      console.error('Feil ved oppdatering:', error);
      alert('Kunne ikke oppdatere klient. Se konsoll for detaljer.');
    }
  };

  const handleSendKYCLink = async (klient, e) => {
    e.stopPropagation();

    if (!klient.epost) {
      alert('Klient mangler e-postadresse. Vennligst legg til e-post først.');
      return;
    }

    try {
      const response = await fetch(`${crmApi.baseUrl}/api/kyc/send-link`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify({
          klient_id: klient.id
        })
      });

      if (!response.ok) {
        throw new Error('Kunne ikke sende KYC-link');
      }

      const data = await response.json();

      // Vis link i alert (senere kan vi sende e-post)
      alert(`✅ KYC-link generert!\n\nKopiér denne lenken og send til klient:\n\n${data.link}\n\nGyldig til: ${new Date(data.utløper).toLocaleString('nb-NO')}`);

    } catch (error) {
      console.error('Feil ved sending av KYC-link:', error);
      alert('Kunne ikke sende KYC-link: ' + error.message);
    }
  };

  const handleSlettKlient = async () => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm(`Er du sikker på at du vil slette klienten "${selectedKlient.navn}"?`)) {
      return;
    }

    try {
      await crmApi.deleteKlient(selectedKlient.id);
      setShowEditModal(false);
      setSelectedKlient(null);
      lastKlienter();
      alert('Klient slettet!');
    } catch (error) {
      console.error('Feil ved sletting:', error);
      alert('Kunne ikke slette klient. Se konsoll for detaljer.');
    }
  };

  const handleEksporterCSV = () => {
    // Generer CSV-data
    const headers = ['Navn', 'Type', 'E-post', 'Telefon', 'Adresse', 'Org.nr', 'Opprettet'];
    const csvData = klienter.map(klient => [
      klient.navn || '',
      klient.type || 'klient',
      klient.epost || '',
      klient.telefon || '',
      klient.adresse || '',
      klient.org_nr || '',
      klient.opprettet ? new Date(klient.opprettet).toLocaleDateString('nb-NO') : ''
    ]);

    // Kombiner headers og data
    const csvContent = [
      headers.join(';'),
      ...csvData.map(row => row.map(cell => `"${cell}"`).join(';'))
    ].join('\n');

    // Last ned som fil
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `klienter_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Filtrer klienter basert på søk og type
  const filtrerteKlienter = klienter.filter(klient => {
    // Type filter
    if (typeFilter !== 'alle' && klient.type !== typeFilter) return false;

    // Søketekst filter
    if (sokTekst) {
      const sok = sokTekst.toLowerCase();
      const matchNavn = klient.navn?.toLowerCase().includes(sok);
      const matchEpost = klient.epost?.toLowerCase().includes(sok);
      const matchTelefon = klient.telefon?.toLowerCase().includes(sok);
      const matchAdresse = klient.adresse?.toLowerCase().includes(sok);
      const matchOrgNr = klient.org_nr?.toLowerCase().includes(sok);

      if (!matchNavn && !matchEpost && !matchTelefon && !matchAdresse && !matchOrgNr) return false;
    }

    return true;
  });

  console.log('🎨 Rendering with:', { loading, klienterCount: klienter?.length });

  if (loading) {
    console.log('⏳ Showing loading state...');
    return (
      <div className="klient-liste loading">
        <div className="spinner"></div>
        <p>Laster klienter...</p>
      </div>
    );
  }

  console.log('✅ Showing main content...');
  return (
    <div className="klient-liste">
      <div className="klient-liste-header">
        <div>
          <h1>👥 Klienter</h1>
          <div style={{display: 'flex', gap: '10px', marginTop: '10px', alignItems: 'center'}}>
            <label style={{fontWeight: 'bold'}}>Vis:</label>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              style={{padding: '8px', borderRadius: '4px', border: '1px solid #ddd'}}
            >
              <option value="alle">Alle</option>
              <option value="klient">Kun klienter</option>
              <option value="forretningsforbindelse">Kun forretningsforbindelser</option>
            </select>
            <span style={{color: '#666', marginLeft: '10px'}}>
              ({filtrerteKlienter.length} av {klienter.length} {typeFilter === 'alle' ? 'totalt' : typeFilter === 'klient' ? 'klienter' : 'forretningsforbindelser'})
            </span>
          </div>
        </div>
        <div style={{display: 'flex', gap: '10px'}}>
          <button className="btn-secondary" onClick={handleEksporterCSV} title="Eksporter klientliste til CSV">
            📊 Eksporter CSV
          </button>
          <button className="btn-secondary" onClick={handleImporterFraExcel}>
            📄 Importer fra Excel
          </button>
          <button className="btn-primary" onClick={handleNyKlient}>
            + Ny klient
          </button>
        </div>
      </div>

      {/* Søkefelt */}
      <div style={{marginBottom: '1.5rem'}}>
        <input
          type="text"
          placeholder="🔍 Søk i klienter (navn, e-post, telefon, adresse, org.nr)..."
          value={sokTekst}
          onChange={(e) => setSokTekst(e.target.value)}
          style={{
            width: '100%',
            padding: '0.75rem',
            fontSize: '1rem',
            border: '1px solid #ddd',
            borderRadius: '8px'
          }}
        />
      </div>

      {/* Statistikk */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1rem',
        marginBottom: '1.5rem'
      }}>
        <div style={{
          background: '#f0f9ff',
          border: '2px solid #0ea5e9',
          borderRadius: '8px',
          padding: '1rem'
        }}>
          <div style={{fontSize: '2rem', fontWeight: 'bold', color: '#0369a1'}}>
            {klienter.filter(k => k.type === 'klient' || !k.type).length}
          </div>
          <div style={{color: '#075985', fontSize: '0.9rem'}}>👥 Klienter</div>
        </div>

        <div style={{
          background: '#f0fdf4',
          border: '2px solid #22c55e',
          borderRadius: '8px',
          padding: '1rem'
        }}>
          <div style={{fontSize: '2rem', fontWeight: 'bold', color: '#16a34a'}}>
            {klienter.filter(k => k.type === 'forretningsforbindelse').length}
          </div>
          <div style={{color: '#15803d', fontSize: '0.9rem'}}>🤝 Forretningsforbindelser</div>
        </div>

        <div style={{
          background: '#fef3c7',
          border: '2px solid #f59e0b',
          borderRadius: '8px',
          padding: '1rem'
        }}>
          <div style={{fontSize: '2rem', fontWeight: 'bold', color: '#d97706'}}>
            {klienter.filter(k => !k.epost).length}
          </div>
          <div style={{color: '#92400e', fontSize: '0.9rem'}}>📧 Mangler e-post</div>
        </div>

        <div style={{
          background: '#fce7f3',
          border: '2px solid #ec4899',
          borderRadius: '8px',
          padding: '1rem'
        }}>
          <div style={{fontSize: '2rem', fontWeight: 'bold', color: '#db2777'}}>
            {Object.keys(kycStatuser).filter(id => kycStatuser[id]?.status === 'fullført').length}
          </div>
          <div style={{color: '#9f1239', fontSize: '0.9rem'}}>✅ KYC godkjent</div>
        </div>
      </div>

      {filtrerteKlienter.length === 0 ? (
        <div className="empty-state">
          <p>{sokTekst ? 'Ingen klienter matchet søket' : 'Ingen klienter funnet'}</p>
          {!sokTekst && (
            <button className="btn-primary" onClick={handleNyKlient}>
              Opprett første klient
            </button>
          )}
        </div>
      ) : (
        <div className="klient-grid">
          {filtrerteKlienter.map(klient => (
            <div
              key={klient.id}
              className="klient-card"
              onClick={() => handleKlientKlikk(klient)}
              style={{cursor: 'pointer'}}
            >
              <div className="klient-card-header">
                <h3>{klient.navn}</h3>
                {getKYCStatusBadge(klient.id)}
              </div>
              {klient.org_nr && (
                <p className="org-nr">Org.nr: {klient.org_nr}</p>
              )}
              {klient.epost && (
                <p className="contact-info">📧 {klient.epost}</p>
              )}
              {klient.telefon && (
                <p className="contact-info">📞 {klient.telefon}</p>
              )}
              {klient.adresse && (
                <p className="contact-info">📍 {klient.adresse}</p>
              )}
              <p className="opprettet">
                Opprettet: {klient.opprettet ? new Date(klient.opprettet).toLocaleDateString('nb-NO', {day: '2-digit', month: '2-digit', year: 'numeric'}) : '-'}
              </p>

              <div className="klient-card-actions">
                <button
                  className="btn-kyc"
                  onClick={(e) => handleStartKYC(klient, e)}
                  title="Start kundekontroll (KYC/AML)"
                >
                  ⚖️ KYC
                </button>
                <button
                  className="btn-send-kyc"
                  onClick={(e) => handleSendKYCLink(klient, e)}
                  title="Send KYC-link til klient"
                  style={{ marginLeft: '8px', backgroundColor: '#059669', color: 'white' }}
                >
                  📧 Send KYC
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Opprett Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Ny klient</h2>

            <div className="form-group">
              <label>Type *</label>
              <select
                value={newKlient.type}
                onChange={(e) => setNewKlient({...newKlient, type: e.target.value})}
                style={{padding: '8px', borderRadius: '4px', border: '1px solid #ddd', width: '100%'}}
                required
              >
                <option value="klient">Klient</option>
                <option value="forretningsforbindelse">Forretningsforbindelse</option>
              </select>
            </div>

            <div className="form-group">
              <label>Navn *</label>
              <input
                type="text"
                value={newKlient.navn}
                onChange={(e) => setNewKlient({...newKlient, navn: e.target.value})}
                placeholder="Fullt navn eller firmanavn"
                required
              />
            </div>

            <div className="form-group">
              <label>E-post</label>
              <input
                type="email"
                value={newKlient.epost}
                onChange={(e) => setNewKlient({...newKlient, epost: e.target.value})}
                placeholder="eksempel@domene.no"
              />
            </div>

            <div className="form-group">
              <label>Telefon/Mobil</label>
              <input
                type="tel"
                value={newKlient.telefon}
                onChange={(e) => setNewKlient({...newKlient, telefon: e.target.value})}
                placeholder="+47 123 45 678"
              />
            </div>

            <div className="form-group">
              <label>Adresse</label>
              <input
                type="text"
                value={newKlient.adresse}
                onChange={(e) => setNewKlient({...newKlient, adresse: e.target.value})}
                placeholder="Gateadresse"
              />
            </div>

            <div className="form-group">
              <label>Organisasjonsnummer (for bedrifter)</label>
              <input
                type="text"
                value={newKlient.org_nr}
                onChange={(e) => setNewKlient({...newKlient, org_nr: e.target.value})}
                placeholder="123456789"
              />
            </div>

            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowCreateModal(false)}>
                Avbryt
              </button>
              <button className="btn-primary" onClick={handleOpprettKlient}>
                Opprett klient
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rediger Modal */}
      {showEditModal && selectedKlient && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Rediger klient</h2>

            <div className="form-group">
              <label>Type *</label>
              <select
                value={editKlient.type}
                onChange={(e) => setEditKlient({...editKlient, type: e.target.value})}
                style={{padding: '8px', borderRadius: '4px', border: '1px solid #ddd', width: '100%'}}
                required
              >
                <option value="klient">Klient</option>
                <option value="forretningsforbindelse">Forretningsforbindelse</option>
              </select>
            </div>

            <div className="form-group">
              <label>Navn *</label>
              <input
                type="text"
                value={editKlient.navn}
                onChange={(e) => setEditKlient({...editKlient, navn: e.target.value})}
                placeholder="Fullt navn eller firmanavn"
                required
              />
            </div>

            <div className="form-group">
              <label>E-post</label>
              <input
                type="email"
                value={editKlient.epost}
                onChange={(e) => setEditKlient({...editKlient, epost: e.target.value})}
                placeholder="eksempel@domene.no"
              />
            </div>

            <div className="form-group">
              <label>Telefon/Mobil</label>
              <input
                type="tel"
                value={editKlient.telefon}
                onChange={(e) => setEditKlient({...editKlient, telefon: e.target.value})}
                placeholder="+47 123 45 678"
              />
            </div>

            <div className="form-group">
              <label>Adresse</label>
              <input
                type="text"
                value={editKlient.adresse}
                onChange={(e) => setEditKlient({...editKlient, adresse: e.target.value})}
                placeholder="Gateadresse"
              />
            </div>

            <div className="form-group">
              <label>Organisasjonsnummer (for bedrifter)</label>
              <input
                type="text"
                value={editKlient.org_nr}
                onChange={(e) => setEditKlient({...editKlient, org_nr: e.target.value})}
                placeholder="123456789"
              />
            </div>

            <div className="modal-actions">
              <button className="btn-danger" onClick={handleSlettKlient}>
                🗑️ Slett
              </button>
              <div style={{flex: 1}}></div>
              <button className="btn-secondary" onClick={() => setShowEditModal(false)}>
                Avbryt
              </button>
              <button className="btn-primary" onClick={handleOppdaterKlient}>
                Lagre endringer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* KYC Modal */}
      {showKYCModal && kycKlient && (
        <div className="modal-overlay" onClick={() => setShowKYCModal(false)}>
          <div className="modal-content kyc-modal-wrapper" onClick={(e) => e.stopPropagation()}>
            <KYCSkjema
              klient={kycKlient}
              onFullført={handleKYCFullført}
              onAvbryt={() => {
                setShowKYCModal(false);
                setKycKlient(null);
              }}
            />
          </div>
        </div>
      )}

      {/* Import fra Excel Modal */}
      {showImportModal && (
        <div className="modal-overlay" onClick={() => setShowImportModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>📄 Importer klienter fra Excel</h2>

            <div style={{marginBottom: '20px'}}>
              <p style={{marginBottom: '15px'}}>
                Excel-filen må inneholde følgende kolonner (valgfritt med store/små bokstaver):
              </p>
              <ul style={{marginLeft: '20px', marginBottom: '15px'}}>
                <li><strong>Navn</strong> (påkrevd) - eller Fornavn + Etternavn</li>
                <li>Telefon eller Mobil (valgfritt)</li>
                <li>E-post eller Email (valgfritt)</li>
                <li>Adresse (valgfritt)</li>
                <li>Org.nr eller Organisasjonsnummer (valgfritt)</li>
                <li>Type - "klient" eller "forretningsforbindelse" (default: klient)</li>
              </ul>
            </div>

            <div className="form-group">
              <label>Velg Excel-fil (.xlsx eller .xls)</label>
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleExcelFileChange}
                style={{
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  width: '100%'
                }}
              />
              {excelFile && (
                <p style={{marginTop: '10px', color: '#28a745'}}>
                  ✓ Valgt fil: {excelFile.name}
                </p>
              )}
            </div>

            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => {
                setShowImportModal(false);
                setExcelFile(null);
              }}>
                Avbryt
              </button>
              <button
                className="btn-primary"
                onClick={handleStartImport}
                disabled={!excelFile}
              >
                Start import
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
